import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  FormControl,
  Box,
  Stack,
  TextField,
  Typography,
  Button,
  Backdrop,
  Alert,
  Grid,
  Container,
  Stepper,
  Step,
  StepButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Dialog
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Language } from "../Language";
import * as api from "../../api/";
import MasterPage from "../../ui-component/MasterPage";
import * as dict from "../../dictionary/Register";
import { checkProductType } from "../../utils/checkProductType";
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

// Top-level reusable tab panel component (stable identity)
const CustomTabPanel = React.memo(function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
});

// Top-level memoized TabTwoPanel to prevent remounts when parent re-renders
const PersonalVerifyForm = React.memo(function TabTwoPanel({
  value,
  customerType,
  last4Id,
  setLast4Id,
  last4Ref,
  dob,
  setDob,
  dobRef,
  remPhone,
  setRemPhone,
  remPhoneRef,
  tab2Error,
  tab2Success,
  submitTabTwo,
  handleChangeCustomerType,
  userLanguage,
}) {
  return (
    <CustomTabPanel value={value} index={1}>
      <Box sx={{ mt: 2 }}>
        <Stack spacing={2}>
          <Typography id="contact_type_label">
            {dict.Contact_Type_TXT[userLanguage]}
          </Typography>
          <Select
            labelId="Contact_Type_label"
            id="contact_type"
            value={customerType}
            onChange={handleChangeCustomerType}
            InputProps={{
              style: {
                borderRadius: "5px",
                border: "1px",
              },
            }}
          >
            <MenuItem value="Thai">
              {dict.Contact_Type_Thai_TXT[userLanguage]}
            </MenuItem>
            <MenuItem value="Foreigner">
              {dict.Contact_Type_Foreigner_TXT[userLanguage]}
            </MenuItem>
          </Select>
          <Typography>
            {(customerType === "Thai" && userLanguage === "th")
              ? "4 ตัวสุดท้ายของบัตรประชาชนหรือพาสปอร์ต"
              : "Last 4 digits of passport number or ID card"}
          </Typography>
          <TextField
            value={last4Id}
            inputRef={last4Ref}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
              setLast4Id(v);
            }}
            label="Last 4 digits"
            inputProps={{ maxLength: 4 }}
          />

          <Typography>
            <Typography>
              {(customerType === "Thai" && userLanguage === "th")
                ? "วันเดือนปีเกิด (ค.ศ.)"
                : "Date of Birth (A.D.)"}
            </Typography>
          </Typography>
          <TextField
            type="date"
            value={dob}
            inputRef={dobRef}
            onChange={(e) => setDob(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Typography>
            {(customerType === "Thai" && userLanguage === "th")
              ? "เบอร์โทรศัพท์"
              : "Phone Number"}
          </Typography>
          <TextField
            value={remPhone}
            inputRef={remPhoneRef}
            onChange={(e) => setRemPhone(e.target.value)}
            label="Phone"
          />

          {tab2Error && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="error">{tab2Error}</Alert>
            </Box>
          )}
          {tab2Success && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success">{tab2Success}</Alert>
            </Box>
          )}

          <Box>
            <Button
              type="button"
              variant="contained"
              onClick={submitTabTwo}
              fullWidth
              sx={{ height: "60px" }}
            >
              {dict.GO_TO_AGREE_TXT[userLanguage]}
            </Button>
          </Box>
        </Stack>
      </Box>
    </CustomTabPanel>
  );
});

// Top-level memoized TabThreePanel for OTP inputs
const ReferenceVerifyForm = React.memo(function TabThreePanel({
  value,
  otpValues,
  setOtpValues,
  otpRefs,
  submitOtp,
  userLanguage,
}) {
  return (
    <CustomTabPanel value={value} index={2}>
      <Box sx={{ mt: 2 }}>
        <Stack spacing={2} alignItems="center">
          <Typography>
            {dict.Contact_OTP_TXT[userLanguage]}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.5, sm: 1 },
              flexWrap: "wrap",
              justifyContent: "center",
              px: { xs: 1, sm: 0 },
            }}
          >
            {otpValues.map((v, i) => (
              <TextField
                key={i}
                inputRef={otpRefs.current[i]}
                value={v}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
                  setOtpValues((prev) => {
                    const next = [...prev];
                    next[i] = val;
                    return next;
                  });
                  if (val) {
                    const nextRef = otpRefs.current?.[i + 1];
                    nextRef?.current?.focus();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace") {
                    e.preventDefault();
                    const cur = otpValues[i];
                    if (cur) {
                      setOtpValues((prev) => {
                        const next = [...prev];
                        next[i] = "";
                        return next;
                      });
                      return;
                    }
                    if (i > 0) {
                      setOtpValues((prev) => {
                        const next = [...prev];
                        next[i - 1] = "";
                        return next;
                      });
                      otpRefs.current[i - 1].current?.focus();
                    }
                  }
                  if (e.key === "ArrowLeft" && i > 0) {
                    otpRefs.current[i - 1].current?.focus();
                  }
                  if (e.key === "ArrowRight" && i < otpValues.length - 1) {
                    otpRefs.current[i + 1].current?.focus();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = (e.clipboardData || window.clipboardData)
                    .getData("text")
                    .replace(/\D/g, "");
                  const chars = paste.slice(0, 6).split("");
                  setOtpValues((prev) => {
                    const next = [...prev];
                    for (let j = 0; j < chars.length; j++) next[j] = chars[j];
                    return next;
                  });
                  const focusIndex = Math.min(chars.length, 5);
                  otpRefs.current[focusIndex].current?.focus();
                }}
                size="small"
                sx={{
                  width: { xs: 36, sm: 44, md: 56 },
                  '& .MuiInputBase-input': { textAlign: 'center', padding: '10px 8px', fontSize: { xs: 14, sm: 16 } },
                }}
                inputProps={{ maxLength: 1 }}
              />
            ))}
          </Box>
          <Box>
            <Button
              variant="contained"
              onClick={submitOtp}
              fullWidth
              sx={{ height: "60px" }}
            >
              {dict.GO_TO_AGREE_TXT[userLanguage]}
            </Button>
          </Box>
        </Stack>
      </Box>
    </CustomTabPanel>
  );
});

// Top-level memoized OTP Verify Form (extracted from inline JSX)
const OtpVerifyForm = React.memo(function OtpVerifyForm({
  value,
  control,
  customerType,
  handleChangeCustomerType,
  requestOTP,
  emailRequestOTP,
  isVisibleInputOTP,
  minutes,
  seconds,
  countTime,
  handleSubmit,
  onSubmit,
  onError,
  userLanguage,
  hiddenSms
}) {
  return (
    <CustomTabPanel value={value} index={0}>
      <Box
        component="form"
        id="hook-form"
        mt={2}
        sx={{ width: "100%" }}
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <Stack spacing={3} direction={"column"} justifyContent={"center"}>
          <Controller
            name="contact_type"
            control={control}
            defaultValue={customerType}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputLabel id="contact_type_label">
                  {dict.Contact_Type_TXT[userLanguage]}
                </InputLabel>
                <Select
                  labelId="Contact_Type_label"
                  id="contact_type"
                  value={customerType}
                  onChange={handleChangeCustomerType}
                  InputProps={{
                    style: {
                      borderRadius: "5px",
                      border: "1px",
                    },
                  }}
                >
                  <MenuItem value="Thai">
                    {dict.Contact_Type_Thai_TXT[userLanguage]}
                  </MenuItem>
                  <MenuItem value="Foreigner">
                    {dict.Contact_Type_Foreigner_TXT[userLanguage]}
                  </MenuItem>
                </Select>
              </>
            )}
          />
          <Controller
            name="citizen_id"
            control={control}
            defaultValue=""
            rules={{
              required: `${customerType === "Thai"
                ? dict.Please_Input_Citizen_TXT[userLanguage]
                : dict.Please_Input_Passport_TXT[userLanguage]
                }`,
            }}
            render={({ field: { onChange, value }, fieldState: { error, rules } }) => (
              <>
                <TextField
                  type={customerType === "Thai" ? "number" : ""}
                  onChange={onChange}
                  label={
                    customerType === "Thai"
                      ? dict.Citizen_Or_Passport_TXT[userLanguage]
                      : dict.Passport_TXT[userLanguage]
                  }
                  InputProps={{
                    style: {
                      borderRadius: "5px",
                      border: "1px",
                    },
                  }}
                />
                {error !== undefined && (
                  <Box sx={{ marginTop: 1 }}>
                    <Alert severity="error">{error.message}</Alert>
                  </Box>
                )}
              </>
            )}
          />
          {customerType === "Thai" && (
            <Grid container alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Grid item xs={7}>
                <Controller
                  name="mobile_no"
                  control={control}
                  rules={{
                    required: hiddenSms ? `${dict.Input_Email_TXT[userLanguage]}` : `${dict.Input_PhoneOrEmail_TXT[userLanguage]}`,
                  }}
                  defaultValue=""
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <TextField
                        onChange={onChange}
                        label={hiddenSms ? dict.Input_Email_TXT[userLanguage] : dict.Phone_Or_Email_TXT[userLanguage]}
                        InputProps={{
                          style: {
                            borderRadius: "5px",
                            border: "1px",
                          },
                        }}
                      />
                      {error !== undefined && (
                        <Box sx={{ marginTop: 1 }}>
                          <Alert severity="error">{error.message}</Alert>
                        </Box>
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={5} sx={{ textAlign: "center" }}>
                <FormControl>
                  {countTime === false && (
                    <Button variant="contained" onClick={() => requestOTP()}>
                      {dict.Send_OTP_TXT[userLanguage]}
                    </Button>
                  )}
                  {countTime === true && (
                    <Button variant="outlined">
                      <Typography>
                        {minutes} : {seconds}
                      </Typography>
                    </Button>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          )}

          {customerType !== "Thai" && (
            <Grid container alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Grid item xs={7}>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: `${dict.Input_Email_TXT[userLanguage]}` }}
                  defaultValue=""
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <TextField
                        onChange={onChange}
                        label={dict.Email_OTP_TXT[userLanguage]}
                        type="email"
                        InputProps={{
                          style: {
                            borderRadius: "5px",
                            border: "1px",
                          },
                        }}
                      />
                      {error !== undefined && (
                        <Box sx={{ marginTop: 1 }}>
                          <Alert severity="error">{error.message}</Alert>
                        </Box>
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={5} sx={{ textAlign: "center" }}>
                <FormControl>
                  {countTime === false && (
                    <Button variant="contained" onClick={() => emailRequestOTP()}>
                      {dict.Send_OTP_TXT[userLanguage]}
                    </Button>
                  )}
                  {countTime === true && (
                    <Button variant="outlined">
                      <Typography>
                        {minutes} : {seconds}
                      </Typography>
                    </Button>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          )}

          {isVisibleInputOTP === true && (
            <Controller
              name="OTP"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormControl sx={{ width: "100%" }}>
                  <TextField
                    type="number"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      minLength: 1,
                      maxLength: 6,
                    }}
                    InputProps={{
                      style: {
                        borderRadius: "5px",
                        border: "1px",
                      },
                    }}
                    onChange={onChange}
                    required
                    label={dict.Fill_OTP_Txt[userLanguage]}
                    helperText={
                      customerType === "Thai"
                        ? dict.Fill_OTP_Helper_Txt[userLanguage]
                        : dict.Email_Fill_OTP_Helper_Txt[userLanguage]
                    }
                  />
                </FormControl>
              )}
            />
          )}
          <Button variant={"contained"} sx={{ height: "60px" }} type={"submit"}>
            {dict.GO_TO_AGREE_TXT[userLanguage]}
          </Button>
        </Stack>
      </Box>
    </CustomTabPanel>
  );
});

const UserInfoModal = ({ data, isOpen, onClose, onSubmitConfrim }) => {
  if (!isOpen) return null;

  const fields = [
    { key: 'full_name' },
    { key: 'mobile' },
    { key: 'email' },
    { key: 'citizen_id' },
  ];

  function maskText(cardNumber) {
  if (cardNumber.length < 4) {
    return cardNumber; // น้อยกว่า 4 ตัว ไม่ปกปิด
  }

  const firstChar = cardNumber.charAt(0);
  const lastTwo = cardNumber.slice(-2);
  const masked = 'X'.repeat(cardNumber.length - 3);

  return `${firstChar}${masked}${lastTwo}`;
}

  return (
    <Dialog
        onClose={() => {
          onClose();
        }}
        open={isOpen}
        disableEscapeKeyDown={true}
        PaperProps={{
            sx: {
                borderRadius: "20px",
            }
        }}
    // onBackdropClick={(e) => e.preventDefault() }
    >
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ padding: "20px 60px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="bg-white rounded-lg shadow-xl w-full h-full max-h-[190vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-800">Verify Info</h2>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {fields.map(({ key, icon: Icon }) => {
              const value = data[key];
              if (!value) return null;
              
              return (
                <div key={key} className="flex items-start space-x-3 py-2 px-4">
                  {/* <div className="mt-1 text-blue-600">
                    <Icon size={20} />
                  </div> */}
                  <div className="flex-1">
                    <p className="text-gray-800 font-medium">
                      {key === 'citizen_id' ? maskText(value) : value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: '24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                backgroundColor: '#6b7280',
                color: 'white',
                fontWeight: '500',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
            >
              Close
            </button>
            <button
              onClick={() => {
                onSubmitConfrim();
              }}
              style={{
                flex: 1,
                backgroundColor: '#2563eb',
                color: 'white',
                fontWeight: '500',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export const Register = () => {
  const [isVisibleLanguage, setIsVisibleLanguage] = useState(false);
  const [activeStep] = useState(0);
  const [loadingBackDrop] = useState(false);
  const [registerStep] = useState(1);
  const [otpAlertType, setOtpAlertType] = useState({ msg: "", type: "" });
  const [validTxtType, setValidTxtType] = useState(null);
  const [warningTxtType, setWarningTxtType] = useState(null);
  const [isVisibleInputOTP, setIsVisibleInputOTP] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpRef, setOtpRef] = useState(null);
  const [countTime, setCountTime] = useState(false);
  const { handleSubmit, control, getValues } = useForm();
  const location = useLocation();
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(0);
  const [userLanguage, setUserLanguage] = useState("th");
  const [customerType, setCustomerType] = useState("Thai");
  const [typeVerifyOTP, setTypeVerifyOTP] = useState("mobile");
  const [value, setValue] = useState(0);
  // Tab two fields
  const [last4Id, setLast4Id] = useState("");
  const [dob, setDob] = useState(""); // we'll store as yyyy-MM-dd from input and convert on submit
  const [remPhone, setRemPhone] = useState("");
  const [tab2Error, setTab2Error] = useState("");
  const [tab2Success, setTab2Success] = useState("");
  const [confrimCustomerData, setConfrimCustomerData] = useState({
    full_name: "",
    mobile: "",
    email: "",
    citizen_id: "",
  });
  const [showDialogConfirm, setShowDialogConfirm] = useState(false)
  const [isShowAuthenCode, setIsShowAuthenCode] = useState(false);
  const [isDisableSendSmsOtp, setDisableSendSmsOtp] = useState(false);
  // Refs for Tab2 inputs to preserve focus across parent re-renders
  const last4Ref = useRef(null);
  const dobRef = useRef(null);
  const remPhoneRef = useRef(null);

  // Tab 3 OTP state: six single-digit inputs
  const [otpValues, setOtpValues] = useState(new Array(6).fill(""));
  // single ref holding an array of input refs
  const otpRefs = useRef(Array.from({ length: 6 }, () => React.createRef()));

  const submitOtp = useCallback(async () => {
    const code = otpValues.join("");
    const lineUserId = localStorage.getItem("userId");
    if (code.length !== 6 || /\D/.test(code)) {
      setOtpAlertType({ msg: "OTP must be 6 digits", type: "error" });
      return;
    }
    // Example: just show success snackbar and clear
    setOtpAlertType({ msg: "Authencode", type: "success" });
    // clear inputs
    setOtpValues(new Array(6).fill(""));
    otpRefs.current[0].current?.focus();

    const otpCodeJoin = otpValues.join('');
    const payload = {
      line_id: lineUserId,
      verlify_code: otpCodeJoin,
      product_type: "remlite",
    }

    const res = await api.referenceCodeVerify(payload);
    console.log("res", res.status);
    if (res.status === 200 && res.data.data && (res.data.data.citizen_id || res.data.data.passport_id)) {
      // navigate("/registration/privacyPolicy" + location.search, {
      //   state: {
      //     citizen_id: (res.data.data.citizen_id) ? res.data.data.citizen_id : res.data.data.passport_id,
      //     line_id: localStorage.getItem("userId"),
      //     mobile_no: res.data.data.mobile_no,
      //     language: userLanguage,
      //     contact_type: customerType,
      //   }
      // });
      const customerDataInfo = res.data?.data
      setConfrimCustomerData({
        full_name: customerType === 'Thai' ? `${customerDataInfo?.firstname} ${customerDataInfo?.lastname}` : `${customerDataInfo?.firstname_en} ${customerDataInfo?.lastname_en}`,
        email: customerDataInfo?.email,
        citizen_id:  customerDataInfo?.citizen_id ? customerDataInfo?.citizen_id : customerDataInfo?.passport_id,
        mobile: customerDataInfo?.mobile
      })
      setShowDialogConfirm(true);
    } else {
      setOtpAlertType({ msg: "otp invalid", type: "error" });
    }

  }, [otpValues, customerType]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let navigate = useNavigate();
  const step = {
    1: `${dict.Step1_TXT[userLanguage]}`,
    2: `${dict.Step2_TXT[userLanguage]}`,
    3: `${dict.Step3_TXT[userLanguage]}`,
    4: `${dict.Step4_TXT[userLanguage]}`,
  };
  const validTxt = {
    otpNoInput: `${dict.Otp_no_input_TXT[userLanguage]}`,
    otpNoVerify: `${dict.Otp_no_verify_TXT[userLanguage]}`,
    otpExpired: `${dict.Otp_expired_TXT[userLanguage]}`,
    idCardInvalid: `${customerType === "Thai"
      ? dict.is_card_invalid_TXT[userLanguage]
      : dict.is_passport_invalid_TXT[userLanguage]
      }`,
  };

  const warningTxt = {
    otpNoPhoneInput: `${dict.Otp_no_phone_or_email_input_TXT[userLanguage]}`,
    otpNoEmailInput: `${dict.Otp_no_email_input_TXT[userLanguage]}`,
  };

  const onSubmit = async (data, e) => {
    if (isVisibleInputOTP === false) {
      setValidTxtType("otpNoInput");
      return;
    }

    let form = getValues();
    if (customerType === "Thai" && typeVerifyOTP === "phone") {
      let verify = await verifyOTP();
      if (verify.status === "204") {
        setValidTxtType("idCardInvalid");
        // setIsVerifyOTP(false)
        return;
      }

      if (verify.status === "205") {
        setValidTxtType("otpExpired");
        // setIsVerifyOTP(false)
        return;
      }

      if (verify.status !== "200") {
        setValidTxtType("otpNoVerify");
        // setIsVerifyOTP(false)
        return;
      }
    } else {
      let verifyEmail = await emailVerifyOTP();
      if (verifyEmail.status === 204) {
        setValidTxtType("idCardInvalid");
        // setIsVerifyOTP(false)
        return;
      }

      if (verifyEmail.status === 205) {
        setValidTxtType("otpExpired");
        // setIsVerifyOTP(false)
        return;
      }

      if (verifyEmail.status !== 200) {
        setValidTxtType("otpNoVerify");
        // setIsVerifyOTP(false)
        return;
      }
    }
    // setIsVerifyOTP(true)
    navigate("/registration/privacyPolicy" + location.search, {
      state: {
        citizen_id: getValues().citizen_id,
        line_id: localStorage.getItem("userId"),
        mobile_no: form.TelephoneNumber,
        language: userLanguage,
        contact_type: customerType,
      },
    });
    // setActiveStep(1)
    return;
  };
  const onError = (errors, e) => console.log(errors);

  const requestOTP = async () => {
    let form = getValues();
    if (form.mobile_no === "") return setWarningTxtType("otpNoPhoneInput");
    setCountTime(true);
    if (countTime === true) return;
    const mobile_no = form.mobile_no;
    const isPhoneNumber = /^[0-9]+$/.test(mobile_no) && isDisableSendSmsOtp === false;
    const isEmail =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        mobile_no
      );
    let response;
    let token = null;

    if (isDisableSendSmsOtp === true && !isEmail) {
      setWarningTxtType("otpNoEmailInput");
      return;
    }
    if (!isPhoneNumber && !isEmail) {
      setWarningTxtType("otpNoPhoneInput");
      return;
    }
    if (isPhoneNumber) {
      setTypeVerifyOTP("phone");
      response = await api.requestOTP({
        line_id: localStorage.getItem("userId"),
        product_type: checkProductType(),
        citizen_id: form.citizen_id,
        mobile_no: form.mobile_no,
      });
      token = response?.data?.token;
    } else if (isEmail) {
      setTypeVerifyOTP("email");
      response = await api.emailRequestOTP({
        line_id: localStorage.getItem("userId"),
        product_type: checkProductType(),
        citizen_id: form.citizen_id,
        email: form.mobile_no,
      });
      token = response?.data?.data?.token;
    }

    if (response?.data?.status === 200) {
      setOtpAlertType({ msg: "", type: "success" });
      setOtpToken(token);
      setIsVisibleInputOTP(true);
      setOtpRef(response?.data?.data?.ref);
      return;
    }
    setOtpAlertType({ msg: response?.response?.data?.message, type: "error" });
    return;
  };

  const verifyOTP = async () => {
    let form = getValues();
    let status = await api.verifyOTP({
      token: otpToken,
      otp_code: form.OTP,
    });

    return status.data;
  };

  // Use the top-level memoized CustomTabPanel defined above to avoid duplicate definitions

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const emailRequestOTP = async () => {
    let form = getValues();
    if (form.email === "") return setWarningTxtType("otpNoEmailInput");
    let response = await api.emailRequestOTP({
      line_id: localStorage.getItem("userId"),
      product_type: checkProductType(),
      citizen_id: form.citizen_id,
      email: form.email,
    });

    setCountTime(true);
    if (response?.data?.status === 200) {
      setOtpAlertType({ msg: "", type: "success" });
      setOtpToken(response?.data?.data?.token);
      setOtpRef(response?.data?.data?.ref);
      setIsVisibleInputOTP(true);
      return;
    }

    setOtpAlertType({ msg: response?.message, type: "error" });
    return;
  };

  const emailVerifyOTP = async () => {
    let form = getValues();
    let status = await api.emailVerifyOTP({
      token: otpToken,
      otp_code: form.OTP,
      ref: otpRef,
    });

    return status.data;
  };

  const handleChooseLanguage = (lang) => {
    setUserLanguage(lang);
  };

  // Helper: convert date input (usually yyyy-MM-dd from <input type=date>) to yyyy/MM/dd for API
  const formatDateToApi = (dateInput) => {
    if (!dateInput) return "";
    // try ISO yyyy-MM-dd first
    const isoMatch = dateInput.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (isoMatch) {
      const [, d, m, y] = isoMatch;
      return `${d}/${m}/${y}`;
    }

    // fallback: try Date parse
    const parsed = new Date(dateInput);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0");
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${d}/${m}/${y}`;
    }

    return "";
  };

  const submitTabTwo = useCallback(
    async (e) => {
      e.preventDefault();
      // Validation using helpers
      if (!isValidLast4Id(last4Id)) {
        setTab2Error("Please enter the last 4 digits of your ID as a 4-digit number");
        return;
      }

      if (!isValidDob(dob)) {
        setTab2Error("Date of birth is not valid (please enter a valid date of birth)");
        return;
      }

      if (!isValidRemPhone(remPhone)) {
        setTab2Error("REM/Rental phone number format is not correct");
        return;
      }

      const formattedDob = formatDateToApi(dob);

      // store for later API calls or navigation
      const payload = {
        last4Id,
        dob: formattedDob,
        remPhone,
        customerType: customerType,
      };
      // save to localStorage as draft
      try {
        localStorage.setItem("registration_tab2", JSON.stringify(payload));
        const customerData = await api.customerInfoVerify({
          line_id: localStorage.getItem("userId"),
          product_type: checkProductType(),
          citizen_id: customerType === 'Thai' ? payload.last4Id : "",
          passport_id: customerType === 'Foreigner' ? payload.last4Id : "",
          birthday: payload.dob,
          mobile: payload.remPhone,
        })
        if (customerData.status !== 200) {
          setTab2Error("Invalid data");
          return
        }
        const verifyCitizenId = customerType === 'Thai' ? customerData.data?.data?.citizen_id : customerData.data?.data?.passport_id;
        const verifyMobileNo = customerData.data?.data?.mobile;
        if (verifyCitizenId && verifyMobileNo) {
          // navigate("/registration/privacyPolicy" + location.search, {
          //   state: {
          //     citizen_id: verifyCitizenId,
          //     line_id: localStorage.getItem("userId"),
          //     mobile_no: verifyMobileNo,
          //     language: userLanguage,
          //     contact_type: customerType,
          //   },
          // });
          const customerDataInfo = customerData.data?.data
          setConfrimCustomerData({
            full_name: customerType === 'Thai' ? `${customerDataInfo?.firstname} ${customerDataInfo?.lastname}` : `${customerDataInfo?.firstname_en} ${customerDataInfo?.lastname_en}`,
            email: customerDataInfo?.email,
            citizen_id:  customerDataInfo?.citizen_id ? customerDataInfo?.citizen_id : customerDataInfo?.passport_id,
            mobile: verifyMobileNo
          })
          // setConfrimCustomerData({
          //   full_name: customerType === 'Thai' ? `${customerDataInfo?.firstname} ${customerDataInfo?.lastname}` : `${customerDataInfo?.firstname_en} ${customerDataInfo?.lastname_en}`,
          //   email: customerDataInfo?.email,
          //   citizen_id:  customerDataInfo?.citizen_id ? customerDataInfo?.citizen_id : customerDataInfo?.passport_id,
          //   mobile: customerDataInfo?.mobile
          // })
          setShowDialogConfirm(true);
        }
      } catch (e) {
        // ignore localStorage errors (e.g., private mode)
      }
      // show success snackbar
      // setOtpAlertType({ msg: "Saved", type: "success" });
      // setTab2Success("บันทึกข้อมูลเรียบร้อย");
    },
    [last4Id, dob, remPhone, customerType]
  );

  const onSubmitConfrim = async (e) => {
    if (confrimCustomerData.citizen_id){
      navigate("/registration/privacyPolicy" + location.search, {
        state: {
          citizen_id: confrimCustomerData.citizen_id,
          line_id: localStorage.getItem("userId"),
          mobile_no: confrimCustomerData.mobile,
          language: userLanguage,
          contact_type: customerType,
        },
      });
    }
  }

  // Validation helpers
  const isValidLast4Id = (s) => {
    return /^[0-9]{4}$/.test(s);
  };

  const isValidDob = (s) => {
    if (!s) return false;
    const parsed = new Date(s);
    if (isNaN(parsed.getTime())) return false;
    const today = new Date();
    // DOB should not be in the future and reasonable past (e.g., after 1900)
    if (parsed > today) return false;
    if (parsed.getFullYear() < 1900) return false;
    return true;
  };

  const isValidRemPhone = (s) => {
    if (!s) return true; // optional
    return /^[0-9+\-\s]{6,20}$/.test(s);
  };

  const isShowAuthenCodeOption = () => {
    let params = new URLSearchParams(window.location.search);
    let isAuthencode = params.get("authencode") === "true" ? true : false;
    setIsShowAuthenCode(isAuthencode);
  }

  const checkIsSendSmsOtp = () => {
    let params = new URLSearchParams(window.location.search);
    let isSendSmsOtp = params.get("disableSMS") === "true" ? true : false;
    setDisableSendSmsOtp(isSendSmsOtp);
  }

  const handleChangeCustomerType = (event) => {
    //alert(event.target.value);
    setCustomerType(event.target.value);
    setIsVisibleInputOTP(false);
  };

  const handleFixMessage = (msg) => {
    if (msg === "Cannot read properties of null (reading 'email')") {
      return dict.Otp_not_found_email_TXT[userLanguage];
    }

    return msg;
  }

  useEffect(() => {
    setIsVisibleLanguage(true);
    isShowAuthenCodeOption()
    checkIsSendSmsOtp()
  }, []);

  useEffect(() => {
    if (seconds === 0 && minutes === 0) {
      setCountTime(false);
    }
  }, [seconds, minutes]);

  useEffect(() => {
    if (countTime === true) {
      setMinutes(1);
      setSeconds(0);
    }
  }, [countTime]);

  // Countdown timer: only run when countTime is true. Use functional updates
  // so we don't rely on stale closure values and avoid re-creating intervals
  // on every render (which caused focus loss and janky behavior).
  useEffect(() => {
    if (!countTime) return undefined;

    const id = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds > 0) return prevSeconds - 1;

        // prevSeconds === 0
        let didDecreaseMinutes = false;
        setMinutes((prevMinutes) => {
          if (prevMinutes > 0) {
            didDecreaseMinutes = true;
            return prevMinutes - 1;
          }
          return 0;
        });

        if (didDecreaseMinutes) {
          // when minutes decreased, set seconds to 59
          return 59;
        }

        // both minutes and seconds are zero -> stop the countdown
        setCountTime(false);
        return 0;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [countTime]);

  return (
    <MasterPage>
      <Box>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: "none",
            height: 60,
          }}
        >
          <Toolbar>
            <Grid container item xs={1}>
              <LanguageIcon onClick={() => setIsVisibleLanguage(true)} />
            </Grid>
            <Grid
              container
              item
              xs={10}
              direction="row"
              justifyContent="center"
            >
              <Typography variant="h6">
                {dict.Registration_TXT[userLanguage]}
              </Typography>
            </Grid>
          </Toolbar>
        </AppBar>
        <Container>
          {registerStep === 4 && (
            <Alert severity="error">{step[registerStep]}</Alert>
          )}

          <Snackbar
            open={otpAlertType?.type === "success"}
            autoHideDuration={6000}
            onClose={() => setOtpAlertType({ msg: "", type: "" })}
          >
            <Alert severity="success">
              "
              {customerType === "Thai"
                ? otpAlertType.msg === "Authencode" 
                ? dict.Authen_Code_Type_Success_TXT[userLanguage]
                : dict.OTP_Type_Success_TXT[userLanguage]
                : dict.OTP_Email_Type_Success_TXT[userLanguage]}
              "
            </Alert>
          </Snackbar>
          <Snackbar
            open={otpAlertType?.type === "error"}
            autoHideDuration={10000}
            onClose={() => setOtpAlertType({ msg: "", type: "" })}
          >
            <Alert severity="error">
              "{dict.OTP_Type_Error_TXT[userLanguage]} {handleFixMessage(otpAlertType?.msg)}"
            </Alert>
          </Snackbar>
          <Snackbar
            open={validTxtType !== null}
            autoHideDuration={10000}
            onClose={() => setValidTxtType(null)}
          >
            <Alert severity="error">{validTxt[validTxtType]}</Alert>
          </Snackbar>
          <Snackbar
            open={warningTxtType !== null}
            autoHideDuration={10000}
            onClose={() => setWarningTxtType(null)}
          >
            <Alert severity="warning">{warningTxt[warningTxtType]}</Alert>
          </Snackbar>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loadingBackDrop}
          >
            <Stack alignItems={"center"}>
              <Typography>{step[registerStep]}</Typography>
              <CircularProgress color="inherit" />
            </Stack>
          </Backdrop>

          <Stepper sx={{ marginTop: 2 }} activeStep={activeStep}>
            <Step>
              <StepButton color="inherit">
                {dict.Registration_TXT[userLanguage]}
              </StepButton>
            </Step>
            <Step>
              <StepButton color="inherit">
                {dict.Agreed_Policy_TXT[userLanguage]}
              </StepButton>
            </Step>
          </Stepper>

          <Box sx={{ width: "100%" }}>
            {isShowAuthenCode && <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="basic tabs example"
              >
                <Tab label="OTP Verify" {...a11yProps(0)} />
                <Tab label="Personal Verify" {...a11yProps(1)} />
                <Tab label="Reference Verify" {...a11yProps(2)} />
              </Tabs>
            </Box>}
            <OtpVerifyForm
              value={value}
              control={control}
              customerType={customerType}
              handleChangeCustomerType={handleChangeCustomerType}
              requestOTP={requestOTP}
              emailRequestOTP={emailRequestOTP}
              isVisibleInputOTP={isVisibleInputOTP}
              minutes={minutes}
              seconds={seconds}
              countTime={countTime}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              onError={onError}
              userLanguage={userLanguage}
              hiddenSms={isDisableSendSmsOtp}
            />
            <PersonalVerifyForm
              value={value}
              customerType={customerType}
              last4Id={last4Id}
              setLast4Id={setLast4Id}
              last4Ref={last4Ref}
              dob={dob}
              setDob={setDob}
              dobRef={dobRef}
              remPhone={remPhone}
              setRemPhone={setRemPhone}
              remPhoneRef={remPhoneRef}
              tab2Error={tab2Error}
              tab2Success={tab2Success}
              submitTabTwo={submitTabTwo}
              userLanguage={userLanguage}
              handleChangeCustomerType={handleChangeCustomerType}
            />
            <ReferenceVerifyForm
              value={value}
              otpValues={otpValues}
              setOtpValues={setOtpValues}
              otpRefs={otpRefs}
              submitOtp={submitOtp}
              userLanguage={userLanguage}
            />
          </Box>
        </Container>

        <Language
          isVisible={isVisibleLanguage}
          response={(lang) => handleChooseLanguage(lang)}
          onClose={() => setIsVisibleLanguage(false)}
          defaultLanguage={userLanguage}
        />
        <UserInfoModal
          data={confrimCustomerData}
          isOpen={showDialogConfirm}
          onClose={() => setShowDialogConfirm(false)}
          onSubmitConfrim={onSubmitConfrim}
        />
      </Box>
    </MasterPage>
  );
};
